import type { MessageFeedback, QuestionAnswer } from 'api/ai-builder/requests';

import { AssistantCompactionMessage } from './assistant-compaction-message';
import { AssistantMaxRoundsMessage } from './assistant-max-rounds-message';
import { AssistantMessage } from './assistant-message';
import { ThinkingMessage } from './thinking-message';
import { ToolMessage } from './tool-message';
import { UserMessage } from './user-message';

import type { ChatMessage } from '../../hooks';

type MessageItemProps = {
  message: ChatMessage;
  isNextTool: boolean;
  isDescriptionOpen: boolean;
  isStreaming?: boolean;
  onToggleDescription: () => void;
  onFeedback?: (messageId: string, feedback: MessageFeedback | null) => void;
  onSubmitDislikeFeedback?: (messageId: string, reasons: string[], details: string) => void;
  onSubmitAnswers: (answers: QuestionAnswer[]) => Promise<void>;
  onPickResource: (selection: { id: string; name: string }) => Promise<void>;
  onAcceptPlan: () => Promise<void>;
  onApproveEnterPlanMode: () => Promise<void>;
  onRejectEnterPlanMode: () => Promise<void>;
  onSubmitMarketplaceAuth: (
    results: { templateShortId: string; isConnected: boolean }[]
  ) => Promise<void>;
  isPlanLoading?: boolean;
  isModeLoading?: boolean;
  isMarketplaceAuthLoading?: boolean;
};

export const MessageItem = ({
  message,
  isNextTool,
  isDescriptionOpen,
  isStreaming,
  onToggleDescription,
  onFeedback,
  onSubmitDislikeFeedback,
  onSubmitAnswers,
  onPickResource,
  onAcceptPlan,
  onApproveEnterPlanMode,
  onRejectEnterPlanMode,
  onSubmitMarketplaceAuth,
  isPlanLoading,
  isModeLoading,
  isMarketplaceAuthLoading,
}: MessageItemProps) => {
  const renderContent = () => {
    if (message.role === 'user') {
      return <UserMessage id={message.id} text={message.text} files={message.files} />;
    }

    if (message.role === 'tool') {
      return (
        <ToolMessage
          toolName={message.toolName}
          toolLabel={message.toolLabel}
          toolDescription={message.toolDescription}
          status={message.status}
          data={message.data}
          errorMessage={message.errorMessage}
          isNextTool={isNextTool}
          isDescriptionOpen={isDescriptionOpen}
          onToggleDescription={onToggleDescription}
          onSubmitAnswers={onSubmitAnswers}
          onPickResource={onPickResource}
          onAcceptPlan={onAcceptPlan}
          onApproveEnterPlanMode={onApproveEnterPlanMode}
          onRejectEnterPlanMode={onRejectEnterPlanMode}
          onSubmitMarketplaceAuth={onSubmitMarketplaceAuth}
          isPlanLoading={isPlanLoading}
          isModeLoading={isModeLoading}
          isMarketplaceAuthLoading={isMarketplaceAuthLoading}
        />
      );
    }

    switch (message.messageType) {
      case 'thinking':
        return <ThinkingMessage text={message.text} timeTakenSeconds={message.timeTakenSeconds} />;
      case 'compaction':
        return <AssistantCompactionMessage text={message.text} />;
      default:
        return message.errorType === 'max_rounds_exceeded' ? (
          <AssistantMaxRoundsMessage text={message.text} />
        ) : (
          <AssistantMessage
            text={message.text}
            errorMessage={message.errorMessage}
            feedbackRating={message.feedbackRating}
            onFeedback={
              onFeedback ? (feedbackRating) => onFeedback(message.id, feedbackRating) : undefined
            }
            onSubmitDislikeFeedback={
              onSubmitDislikeFeedback
                ? (reasons, details) => onSubmitDislikeFeedback(message.id, reasons, details)
                : undefined
            }
            isStreaming={isStreaming}
          />
        );
    }
  };

  return <div data-message-id={message.id}>{renderContent()}</div>;
};
